/**
 * Boundary coercions for model constructors.
 *
 * Every model here is built the same way: spread a defaults object, spread
 * `...init` over it, then assign each field through. The defaults are therefore
 * decorative whenever the API sends the key AT ALL -- `permissions: []` is
 * replaced by whatever `data.permissions` held, including `null`, a string or
 * an object. With `strict: false`, nothing else checks.
 *
 * That has produced repeated review findings, each fixed one field at a time
 * (SD-4458). The rule for what needs coercing is not "could the API send
 * something odd" but "does a consumer call a type-specific method on it" --
 * `.includes`, `.replace`, `.slice`, `.map`. Those throw inside a render rather
 * than displaying oddly, and twice that render was a modal, so the throw took
 * the whole detail view rather than one field.
 *
 * Use these in the constructor AFTER the spread. They are deliberately dumb:
 * this is a coercion pass, not schema validation, and the repo has no zod.
 *
 * Note what they do NOT do. `asString(0)` is `""`, not `"0"`: a number where a
 * string belongs is a contract break, and quietly stringifying it would hide
 * that while leaving a value no consumer expects. Same for `asArray` -- a bare
 * string is not wrapped into a one-element array, because a consumer calling
 * `.map` on it wanted a list, not one item it happened to be able to split.
 */

/** A string, or `""` for anything that is not one. */
export const asString = (value: any): string =>
  typeof value === "string" ? value : "";

/** An array, or `[]` for anything that is not one. */
export const asArray = <T = any>(value: any): T[] =>
  Array.isArray(value) ? value : [];
